import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { auth, db, firebaseReady } from '../../firebase';
import compressImage from '../../utils/compressImage';
import linkifyText from '../../utils/linkifyText';
import ImageLightbox from '../common/ImageLightbox';
import './SupportWidget.css';

const STORAGE_KEY = 'ciphr_support_chat_id';
const TYPING_IDLE_MS = 1500;
const AUTO_REPLY_THINK_DELAY_MS = 1200; // pause before the "typing" indicator appears
const AUTO_REPLY_TYPING_DURATION_MS = 1800; // how long it shows "typing" before the message lands

function buildAutoReplyText(name) {
  return `Hello ${name}, welcome to COTI Official Support! 👋
One of our support team members will assist you shortly.

Please stay in the chat and refresh the page occasionally to make sure you don’t miss our response.

Thanks for your patience!`;
}

// Best-effort notification only - Firestore is the source of truth for
// the chat itself. If this fails (Netlify function down, bad env vars,
// offline), the visitor's message still saved fine; we just log it.
function notifyTelegram(payload) {
  fetch('/.netlify/functions/notify-telegram', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('Telegram notify failed:', err);
  });
}

function toMillis(ts) {
  return ts && typeof ts.toMillis === 'function' ? ts.toMillis() : 0;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Fire-and-forget: gives the auto-reply a natural pause instead of
// landing the instant the visitor's message saves, and shows the
// existing "Support is typing…" indicator to bridge the gap.
async function scheduleAutoReply(chatRef, name) {
  try {
    await wait(AUTO_REPLY_THINK_DELAY_MS);
    await setDoc(chatRef, { supportTyping: true }, { merge: true });
    await wait(AUTO_REPLY_TYPING_DURATION_MS);
    await addDoc(collection(chatRef, 'messages'), {
      sender: 'system',
      text: buildAutoReplyText(name),
      createdAt: serverTimestamp(),
    });
    await setDoc(
      chatRef,
      { lastMessageAt: serverTimestamp(), supportTyping: false },
      { merge: true }
    );
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Auto-reply failed:', err);
  }
}

// Small inline icon so we're not pulling in an icon library for one glyph.

function AttachIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M16.5 6.5L8.5 14.5C7.4 15.6 7.4 17.4 8.5 18.5C9.6 19.6 11.4 19.6 12.5 18.5L19.5 11.5C21.4 9.6 21.4 6.4 19.5 4.5C17.6 2.6 14.4 2.6 12.5 4.5L5.5 11.5C2.9 14.1 2.9 18.3 5.5 20.9"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/**
 * SupportWidget
 * The "Live Support" launcher lives on every screen (rendered once in
 * App.js, fixed position, so it stays put while the page scrolls).
 * Clicking it opens a small panel in the corner - not a full-screen
 * takeover.
 *
 * First visit: a short form (name / subject / message / email).
 * Submitting it creates a chat in Firestore and immediately switches to
 * a chat view. Return visits (same browser): the chat id is remembered
 * in localStorage, so the panel opens straight into the conversation -
 * just a message list and a plain reply box, no form fields again.
 *
 * No account or login: every visitor is signed in anonymously via
 * Firebase Auth behind the scenes so Firestore security rules can still
 * scope access to "your own chat" without ever showing a login screen.
 *
 * Images: compressed client-side (src/utils/compressImage.js) and
 * stored as a base64 data URL directly on the message document -
 * deliberately not Firebase Storage, since new Firebase projects need
 * the Blaze plan just to get a Storage bucket. This keeps the whole
 * feature on the free Firestore tier the rest of the chat already uses.
 */
export default function SupportWidget() {
  const [open, setOpen] = useState(false);
  const [uid, setUid] = useState(null);
  const [chatId, setChatId] = useState(() => localStorage.getItem(STORAGE_KEY));
  const [chatMeta, setChatMeta] = useState(null);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState({ fullName: '', subject: '', body: '', email: '' });
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [chatError, setChatError] = useState('');
  const [connectionIssue, setConnectionIssue] = useState(false);
  const [brokenImages, setBrokenImages] = useState({});
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  const fileInputRef = useRef(null);

  // Anonymous auth - happens once, silently, no UI.
  useEffect(() => {
    if (!firebaseReady) return undefined;
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUid(user.uid);
      } else {
        signInAnonymously(auth).catch(() => {
          // If auth fails (e.g. offline), the widget still renders the
          // form; sending will simply retry auth at submit time.
        });
      }
    });
    return () => unsub();
  }, []);

  // Once we have a chat id, subscribe to the chat document itself (for
  // typing status + read receipts) and to its messages, both in real
  // time, so support replies / typing / read state show up without the
  // visitor refreshing anything.
  useEffect(() => {
    if (!chatId || !firebaseReady) return undefined;
    const unsub = onSnapshot(
      doc(db, 'supportChats', chatId),
      (snap) => setChatMeta(snap.data() || null),
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Chat listener error:', err);
        setConnectionIssue(true);
      }
    );
    return () => unsub();
  }, [chatId]);

  useEffect(() => {
    if (!chatId || !firebaseReady) return undefined;
    const messagesRef = collection(db, 'supportChats', chatId, 'messages');
    const q = query(messagesRef, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        setConnectionIssue(false);
        setMessages(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error('Messages listener error:', err);
        setConnectionIssue(true);
      }
    );
    return () => unsub();
  }, [chatId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Mark the conversation read (from the visitor's side) whenever the
  // panel is actually open and showing the chat - not just subscribed
  // to it in the background.
  useEffect(() => {
    if (!open || !chatId || !firebaseReady) return;
    setDoc(doc(db, 'supportChats', chatId), { visitorLastReadAt: serverTimestamp() }, { merge: true }).catch(
      () => {}
    );
  }, [open, chatId, messages]);

  async function ensureAuth() {
    if (!firebaseReady) throw new Error('Firebase is not configured.');
    if (uid) return uid;
    const cred = await signInAnonymously(auth);
    setUid(cred.user.uid);
    return cred.user.uid;
  }

  function setTyping(value) {
    if (!chatId || !firebaseReady) return;
    if (isTypingRef.current === value) return;
    isTypingRef.current = value;
    setDoc(doc(db, 'supportChats', chatId), { visitorTyping: value }, { merge: true }).catch(() => {});
  }

  function handleReplyChange(e) {
    setReply(e.target.value);
    if (!chatId) return;
    setTyping(true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => setTyping(false), TYPING_IDLE_MS);
  }

  async function handleFormSubmit(e) {
    e.preventDefault();
    if (!form.fullName.trim() || !form.body.trim() || !form.email.trim() || sending) return;
    if (!firebaseReady) {
      setError("Support chat isn't connected yet - see .env.example.");
      return;
    }
    setSending(true);
    setError('');
    try {
      const visitorUid = await ensureAuth();
      const chatRef = doc(collection(db, 'supportChats'));

      await setDoc(chatRef, {
        fullName: form.fullName.trim(),
        subject: form.subject.trim() || 'Support request',
        email: form.email.trim(),
        visitorUid,
        status: 'open',
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'supportChats', chatRef.id, 'messages'), {
        sender: 'visitor',
        text: form.body.trim(),
        createdAt: serverTimestamp(),
      });

      notifyTelegram({
        text: form.body.trim(),
        subject: form.subject.trim() || 'Support request',
        email: form.email.trim(),
        fullName: form.fullName.trim(),
      });

      localStorage.setItem(STORAGE_KEY, chatRef.id);
      setChatId(chatRef.id);
      setForm({ fullName: '', subject: '', body: '', email: '' });

      // Not awaited on purpose - the chat view above already opened
      // with their own message in it; the auto-reply lands a couple
      // seconds later on its own, "typing…" indicator and all.
      scheduleAutoReply(chatRef, form.fullName.trim());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Support form submit failed:', err);
      setError('Something went wrong sending that. Please try again.');
    } finally {
      setSending(false);
    }
  }

  async function handleReplySend(e) {
    e.preventDefault();
    const text = reply.trim();
    if (!text || !chatId || sending || !firebaseReady) return;
    // Clear immediately - don't wait on the network round-trip before
    // the input feels responsive.
    setReply('');
    clearTimeout(typingTimeoutRef.current);
    setTyping(false);
    setSending(true);
    setChatError('');
    try {
      await ensureAuth();
      await addDoc(collection(db, 'supportChats', chatId, 'messages'), {
        sender: 'visitor',
        text,
        createdAt: serverTimestamp(),
      });
      await setDoc(
        doc(db, 'supportChats', chatId),
        { lastMessageAt: serverTimestamp() },
        { merge: true }
      );
      notifyTelegram({ text, chatId });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Reply send failed:', err);
      setChatError('Something went wrong sending that. Please try again.');
    } finally {
      setSending(false);
    }
  }

  async function handleImageSelect(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking the same file again later
    if (!file || !chatId || !firebaseReady) return;
    setChatError('');
    setUploadingImage(true);
    try {
      const dataUrl = await compressImage(file);
      await ensureAuth();
      await addDoc(collection(db, 'supportChats', chatId, 'messages'), {
        sender: 'visitor',
        type: 'image',
        imageData: dataUrl,
        createdAt: serverTimestamp(),
      });
      await setDoc(
        doc(db, 'supportChats', chatId),
        { lastMessageAt: serverTimestamp() },
        { merge: true }
      );
      notifyTelegram({ text: '📷 Sent an image', chatId });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Image send failed:', err);
      setChatError(err.message || 'Could not send that image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  }

  const supportReadAtMillis = toMillis(chatMeta?.supportLastReadAt);

  return (
    <>
      <button
        type="button"
        className="support-fab"
        aria-label={open ? 'Close support chat' : 'Open live support chat'}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`support-fab__icon ${open ? 'support-fab__icon--close' : ''}`}>
          <span className="support-fab__icon-inner">
            {open ? (
              <span className="support-fab__x">×</span>
            ) : (
              <img src="/logo-mark.svg" alt="" className="support-fab__logo" />
            )}
          </span>
          <span className="support-fab__status-dot" aria-hidden="true" />
        </span>
        <span className="support-fab__label">{open ? 'Close chat' : 'Live Support'}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="support-panel"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-label="Contact support"
          >
            <div className="support-panel__header">
              <span className="support-panel__title">
                {chatId && (
                  <img src="/logo-mark.svg" alt="" className="support-panel__title-logo" aria-hidden="true" />
                )}
                {chatId ? 'Live Support Chat' : 'Contact us'}
                {chatId && <span className="support-panel__live-dot" aria-hidden="true" />}
              </span>
              <button
                type="button"
                className="support-panel__close"
                aria-label="Close"
                onClick={() => setOpen(false)}
              >
                ×
              </button>
            </div>

            {connectionIssue && (
              <p className="support-connection-notice">
                Connection hiccup - if messages stop updating, try reopening this panel.
              </p>
            )}

            {!chatId ? (
              <form className="support-form" onSubmit={handleFormSubmit}>
                <label>
                  Full name
                  <input
                    required
                    type="text"
                    value={form.fullName}
                    onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                    placeholder="Your name"
                  />
                </label>
                <label>
                  Subject
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                    placeholder="What's this about?"
                  />
                </label>
                <label>
                  Message
                  <textarea
                    required
                    rows={4}
                    value={form.body}
                    onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                    placeholder="How can we help?"
                  />
                </label>
                <label>
                  Your email
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="you@example.com"
                  />
                </label>
                {error && <p className="support-form__error">{error}</p>}
                <button type="submit" className="btn btn--solid" disabled={sending}>
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </form>
            ) : (
              <>
                <div className="support-messages" ref={scrollRef}>
                  {messages.map((m) => {
                    // Explicit check (not "!== support") so the auto-
                    // reply, sent as sender: 'system', renders on the
                    // support side rather than being mistaken for
                    // something the visitor said.
                    const isVisitor = m.sender === 'visitor';
                    const createdMillis = toMillis(m.createdAt);
                    const isRead =
                      isVisitor && createdMillis > 0 && supportReadAtMillis >= createdMillis;
                    const isImage = m.type === 'image';
                    return (
                      <div
                        key={m.id}
                        className={`support-bubble support-bubble--${isVisitor ? 'visitor' : 'support'} ${
                          isImage ? 'support-bubble--image' : ''
                        }`}
                      >
                        {isImage ? (
                          brokenImages[m.id] ? (
                            <span className="chat-image-error">⚠ Image failed to load</span>
                          ) : (
                            <button
                              type="button"
                              className="chat-image-wrap"
                              onClick={() => setLightboxSrc(m.imageData)}
                            >
                              <img
                                src={m.imageData}
                                alt="Sent attachment"
                                onError={() =>
                                  setBrokenImages((prev) => ({ ...prev, [m.id]: true }))
                                }
                              />
                            </button>
                          )
                        ) : (
                          linkifyText(m.text)
                        )}
                        {isVisitor && (
                          <span className={`support-tick ${isRead ? 'support-tick--read' : ''}`}>
                            {isRead ? '✓✓' : '✓'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {chatMeta?.supportTyping && (
                    <div className="support-typing">Support is typing…</div>
                  )}
                </div>

                {chatError && <p className="support-chat-error">{chatError}</p>}

                <form className="support-reply" onSubmit={handleReplySend}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="support-file-input"
                    onChange={handleImageSelect}
                  />
                  <button
                    type="button"
                    className="support-attach-btn"
                    aria-label="Attach an image"
                    disabled={uploadingImage}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploadingImage ? <span className="support-mini-spinner" /> : <AttachIcon />}
                  </button>
                  <input
                    type="text"
                    value={reply}
                    onChange={handleReplyChange}
                    placeholder="Type a reply…"
                    aria-label="Type a reply"
                  />
                  <button type="submit" disabled={sending} aria-label="Send reply">
                    ↑
                  </button>
                </form>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </>
  );
}